import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Select } from 'antd';
import { Building2 } from 'lucide-react';
import { useGetCompanyGroupQuery } from '../store';
import { useAuth } from './AuthContext';

interface CompanyGroupOption {
  label: string;
  value: number;
}

interface CompanyScopeContextValue {
  companyGroupId?: number;
  companyGroups: CompanyGroupOption[];
  isSystemAdmin: boolean;
  isLoading: boolean;
  setCompanyGroupId: (value?: number) => void;
}

const STORAGE_KEY = 'adminCompanyGroupScope';
const CompanyScopeContext = createContext<CompanyScopeContextValue | null>(
  null,
);

interface CompanyGroupRecord {
  id?: number;
  ID?: number;
  name?: string;
  Name?: string;
  description?: string;
  ShortDescription?: string;
}

const normalizeCompanyGroups = (response: unknown): CompanyGroupOption[] => {
  const payload = response as {
    data?: CompanyGroupRecord[] | { list?: CompanyGroupRecord[] };
    list?: CompanyGroupRecord[];
  };
  const records = Array.isArray(payload?.data)
    ? payload.data
    : (payload?.data?.list ?? payload?.list ?? []);

  if (!Array.isArray(records)) return [];

  return records
    .map(record => ({
      value: Number(record?.id ?? record?.ID),
      label:
        record?.name ??
        record?.Name ??
        record?.description ??
        record?.ShortDescription ??
        `Company group ${record?.id ?? record?.ID}`,
    }))
    .filter(
      (option: CompanyGroupOption) =>
        Number.isInteger(option.value) && option.value > 0,
    );
};

export const CompanyScopeProvider: React.FC<{
  children: React.ReactNode;
  enabled?: boolean;
}> = ({ children, enabled = true }) => {
  const { user } = useAuth();
  const isSystemAdmin = Boolean(
    user?.userPanelRoles?.some(
      item => item?.role?.name?.toUpperCase() === 'ADMIN',
    ),
  );
  const [companyGroupId, setCompanyGroupIdState] = useState<number | undefined>(
    () => {
      const saved = Number(sessionStorage.getItem(STORAGE_KEY));
      return Number.isInteger(saved) && saved > 0 ? saved : undefined;
    },
  );
  const companyGroupResponse = useGetCompanyGroupQuery(undefined, {
    skip: !isSystemAdmin || !enabled,
  });
  const companyGroups = useMemo(
    () => normalizeCompanyGroups(companyGroupResponse.data),
    [companyGroupResponse.data],
  );

  useEffect(() => {
    if (!isSystemAdmin) {
      setCompanyGroupIdState(undefined);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [isSystemAdmin]);

  const setCompanyGroupId = (value?: number) => {
    setCompanyGroupIdState(value);
    if (value) sessionStorage.setItem(STORAGE_KEY, String(value));
    else sessionStorage.removeItem(STORAGE_KEY);
  };

  return (
    <CompanyScopeContext.Provider
      value={{
        companyGroupId,
        companyGroups,
        isSystemAdmin,
        isLoading: companyGroupResponse.isFetching,
        setCompanyGroupId,
      }}
    >
      {children}
    </CompanyScopeContext.Provider>
  );
};

export const useCompanyScope = () => {
  const context = useContext(CompanyScopeContext);
  if (!context) {
    throw new Error('Company scope must be used inside CompanyScopeProvider.');
  }
  return context;
};

export const CompanyScopeSelector: React.FC<{
  className?: string;
  compact?: boolean;
}> = ({ className, compact = false }) => {
  const {
    companyGroupId,
    companyGroups,
    isSystemAdmin,
    isLoading,
    setCompanyGroupId,
  } = useCompanyScope();

  if (!isSystemAdmin) return null;

  return (
    <div
      className={`flex items-center border border-slate-200 bg-white shadow-2xs shrink-0 transition-all min-w-0 ${compact
        ? 'h-8 w-[175px] gap-1 rounded-md px-2'
        : 'h-11 w-56 sm:w-64 gap-2.5 rounded-xl px-3 py-1.5'
        } ${className ?? ''}`}
    >
      <div
        className={`bg-indigo-50 text-indigo-600 shrink-0 ${compact ? 'rounded-md p-1' : 'rounded-lg p-1.5'
          }`}
      >
        <Building2 size={compact ? 10 : 15} />
      </div>
      <div className="min-w-0 flex-1 overflow-hidden">
        {!compact && (
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 truncate">
            Company scope
          </div>
        )}
        <Select
          aria-label="Company scope"
          bordered={false}
          size="small"
          className="w-[175px] text-xs font-medium text-slate-800 whitespace-nowrap"
          // style={{ width: '100%', height: compact ? 28 : undefined }}
          loading={isLoading}
          optionFilterProp="label"
          options={[
            { label: 'All company groups', value: 0 },
            ...companyGroups,
          ]}
          showSearch
          popupMatchSelectWidth={false}
          value={companyGroupId ?? 0}
          onChange={value => setCompanyGroupId(value || undefined)}
        />
      </div>
    </div>
  );
};
